'use client';

import { Scientist } from '@/data/atomHistory';
import { motion } from 'framer-motion';
import { Award, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

interface ScientistCardProps {
  scientist: Scientist;
  index: number;
}

export function ScientistCard({ scientist, index }: ScientistCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <motion.div
        whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(100, 255, 218, 0.2)' }}
        transition={{ duration: 0.2 }}
      >
        <Card className="h-full dark:bg-[#112240] light:bg-white dark:border-[#233554] light:border-gray-200 overflow-hidden">
          {/* Header with gradient */}
          <div className="h-24 bg-gradient-to-br from-[#64ffda] to-[#82aaff] relative">
            <div className="absolute inset-0 bg-black/20" />
          </div>

          <CardHeader className="relative -mt-12 pb-4">
            {/* Avatar placeholder */}
            <div className="w-24 h-24 mx-auto rounded-full dark:bg-[#0a192f] light:bg-gray-100 border-4 dark:border-[#112240] light:border-white flex items-center justify-center">
              <span className="text-4xl font-bold dark:text-[#64ffda] light:text-teal-600">
                {scientist.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>

            <CardTitle className="text-center mt-3 dark:text-white light:text-gray-900">
              {scientist.name}
            </CardTitle>
            <p className="text-center text-sm dark:text-[#a8b2d1] light:text-gray-600">
              {scientist.years}
            </p>
          </CardHeader>

          <CardContent className="space-y-3">
            {/* Nationality */}
            <div className="flex items-center gap-2 text-sm dark:text-[#a8b2d1] light:text-gray-600">
              <MapPin className="w-4 h-4 dark:text-[#64ffda] light:text-teal-600" />
              <span>{scientist.nationality}</span>
            </div>

            {/* Nobel Prize */}
            {scientist.nobelPrize && (
              <Badge
                variant="secondary"
                className="w-full justify-center gap-2 dark:bg-[#64ffda]/10 light:bg-teal-50 dark:text-[#64ffda] light:text-teal-700 dark:border-[#64ffda]/30 light:border-teal-200"
              >
                <Award className="w-4 h-4" />
                {scientist.nobelPrize}
              </Badge>
            )}

            {/* Contribution */}
            <p className="text-sm dark:text-[#ccd6f6] light:text-gray-700 leading-relaxed pt-2 border-t dark:border-[#233554] light:border-gray-200">
              {scientist.contribution}
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
